import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { SignalDetail } from '../types/signals-live';

export type ViewType = 'signal' | 'taxonomy' | 'signals' | 'today' | 'root-causes' | 'reporting' | 'settings' | null;

export interface TaxonomyData {
  id: string;
  name: string;
  categories: any[];
  signalCount: number;
  lastUpdated: Date;
}

export interface JunoViewContext {
  signalId?: string;
  signalData?: SignalDetail;
  taxonomyId?: string;
  taxonomyData?: TaxonomyData;
  relatedItems?: any[];
  dashboardSummary?: any; // Dashboard summary for today view
}

export interface JunoContextData {
  viewType: ViewType;
  viewContext: JunoViewContext;
  isJunoVisible: boolean;
  isJunoActive: boolean;
  toggleJuno: () => void;
  setJunoActive: (active: boolean) => void;
  updateViewContext: (type: ViewType, context: Partial<JunoViewContext>) => void;
  clearViewContext: () => void;
}

const JunoContext = createContext<JunoContextData | undefined>(undefined);

export const useJunoContext = () => {
  const context = useContext(JunoContext);
  if (!context) {
    throw new Error('useJunoContext must be used within a JunoProvider');
  }
  return context;
};

interface JunoProviderProps {
  children: ReactNode;
}

export const JunoProvider: React.FC<JunoProviderProps> = ({ children }) => {
  const [viewType, setViewType] = useState<ViewType>(null);
  const [viewContext, setViewContext] = useState<JunoViewContext>({});
  const [isJunoVisible, setIsJunoVisible] = useState(false);
  const [isJunoActive, setIsJunoActive] = useState(false);

  const toggleJuno = useCallback(() => {
    setIsJunoVisible(prev => !prev);
  }, []);

  const setJunoActiveHandler = useCallback((active: boolean) => {
    setIsJunoActive(active);
    if (active) {
      setIsJunoVisible(true);
    }
  }, []);

  const updateViewContext = useCallback((type: ViewType, context: Partial<JunoViewContext>) => {
    setViewType(type);
    setViewContext(prev => ({
      ...prev,
      ...context
    }));
  }, []);

  const clearViewContext = useCallback(() => {
    setViewType(null);
    setViewContext({});
  }, []);

  const value: JunoContextData = {
    viewType,
    viewContext,
    isJunoVisible,
    isJunoActive,
    toggleJuno,
    setJunoActive: setJunoActiveHandler,
    updateViewContext,
    clearViewContext
  };

  return (
    <JunoContext.Provider value={value}>
      {children}
    </JunoContext.Provider>
  );
};

export default JunoContext;